import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { ClubBook, ReadingFormat } from "@pagemates/shared";
import { api, describeError } from "../lib/api";
import { CloseIcon, SyncIcon } from "./Icons";
import { GiveUpInline } from "./FinishCards";
import { duration, pageAt, secondsAt, type PosCtx } from "../lib/format";

/*
 * The one progress editor: pages, percent or audio, with the audiobook's
 * length asked for once when Hardcover didn't know it; Save, "Done with
 * it", and the sitting-out link where that applies. Your block on Reading
 * and the finished-book panel both use it, so the offer is the same.
 */
export function ProgressEditor({ clubId, book, percent, myFormat, source, done, onClose, onSaved, sitOut }:
  { clubId: string; book: ClubBook; percent: number; myFormat: ReadingFormat; source: "manual" | "hardcover" | null;
    done: boolean; onClose?: () => void; onSaved?: () => void; sitOut?: () => void }) {
  const qc = useQueryClient();
  const pages = book.book.pages;
  const [format, setFormat] = useState<ReadingFormat>(myFormat);
  const [value, setValue] = useState(percent); // percent, whatever the unit typed
  const [pageIn, setPageIn] = useState<number | "">("");
  const [pctIn, setPctIn] = useState<number | "">("");
  const [hIn, setHIn] = useState<number | "">("");
  const [mIn, setMIn] = useState<number | "">("");
  const [lenH, setLenH] = useState<number | "">(""); // the audiobook's length, when Hardcover didn't know it
  const [lenM, setLenM] = useState<number | "">("");
  const [giving, setGiving] = useState(false);
  const lenTyped = lenH !== "" || lenM !== "" ? (Number(lenH || 0) * 3600 + Number(lenM || 0) * 60) : 0;
  const audio = book.book.audioSeconds ?? (lenTyped > 0 ? lenTyped : null);
  useEffect(() => {
    setValue(percent); setFormat(myFormat);
    setPageIn(pages ? pageAt(percent, pages) : ""); setPctIn(percent);
    const secs = audio ? secondsAt(percent, audio) : 0; setHIn(Math.floor(secs / 3600)); setMIn(Math.round((secs % 3600) / 60));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [percent, myFormat, pages, book.book.audioSeconds]);
  const save = useMutation({
    mutationFn: async () => {
      const newLen = format === "audio" && !book.book.audioSeconds && lenTyped > 0;
      if (format !== myFormat || newLen) await api.setFormat(clubId, book.id, format, newLen ? lenTyped : undefined);
      if (value !== percent) await api.updateProgress(clubId, book.id, { percent: value });
    },
    onSuccess: async () => { await qc.invalidateQueries({ queryKey: ["books", clubId] }); await qc.invalidateQueries({ queryKey: ["discussion", clubId, book.id] }); onSaved?.(); },
  });
  const clamp = (n: number) => Math.max(0, Math.min(100, Math.round(n)));
  function fromPage(v: number | "") { setPageIn(v); if (v !== "" && pages) setValue(clamp((v / pages) * 100)); }
  function fromPct(v: number | "") { setPctIn(v); if (v !== "") setValue(clamp(v)); }
  function fromTime(h: number | "", m: number | "") { setHIn(h); setMIn(m); if (audio) setValue(clamp((((h || 0) * 3600 + (m || 0) * 60) / audio) * 100)); }
  const live: PosCtx = { pages, audioSeconds: audio, myFormat: format };

  if (giving) return <div className="giving"><GiveUpInline clubId={clubId} book={book} onDone={() => setGiving(false)} /></div>;
  return (
    <form className="editor" onSubmit={(e) => { e.preventDefault(); save.mutate(); }}>
      <div className="top-line">
        <div className="seg units" role="radiogroup" aria-label="How you count this book">
          {pages && <button type="button" aria-pressed={format === "pages"} onClick={() => setFormat("pages")}>Pages</button>}
          <button type="button" aria-pressed={format === "percent"} onClick={() => setFormat("percent")}>Percent</button>
          <button type="button" aria-pressed={format === "audio"} onClick={() => setFormat("audio")}>Audio</button>
        </div>
        {onClose && <button type="button" className="x" aria-label="Close without saving" onClick={onClose}><CloseIcon width={18} height={18} /></button>}
      </div>
      {format === "audio" && !book.book.audioSeconds && (
        <div className="line ask-len">
          <span>How long is the audiobook?</span>
          <input id="audio-len-h" className="input num sm" type="number" min={0} max={400} value={lenH} onChange={(e) => setLenH(e.target.value === "" ? "" : Number(e.target.value))} aria-label="Hours long" /><span>h</span>
          <input id="audio-len-m" className="input num sm" type="number" min={0} max={59} value={lenM} onChange={(e) => setLenM(e.target.value === "" ? "" : Number(e.target.value))} aria-label="Minutes long" /><span>m</span>
        </div>
      )}
      <div className="line">
        {format === "pages" && pages ? (
          <><span>p.</span><input id="progress-page" className="input num" type="number" min={0} max={pages} value={pageIn} onChange={(e) => fromPage(e.target.value === "" ? "" : Number(e.target.value))} aria-label="Page" /><span>of {pages}</span></>
        ) : format === "audio" && audio ? (
          <><input id="progress-h" className="input num sm" type="number" min={0} max={Math.ceil(audio / 3600)} value={hIn} onChange={(e) => fromTime(e.target.value === "" ? "" : Number(e.target.value), mIn)} aria-label="Hours" /><span>h</span>
            <input id="progress-m" className="input num sm" type="number" min={0} max={59} value={mIn} onChange={(e) => fromTime(hIn, e.target.value === "" ? "" : Number(e.target.value))} aria-label="Minutes" /><span>m</span><span>of {duration(audio)}</span></>
        ) : (
          <><input id="progress-pct" className="input num" type="number" min={0} max={100} value={pctIn} onChange={(e) => fromPct(e.target.value === "" ? "" : Number(e.target.value))} aria-label="Percent" /><span>%</span></>
        )}
        <button type="submit" className="btn primary sm" disabled={save.isPending || (value === percent && format === myFormat) || (format === "audio" && !audio)}>
          {save.isPending ? "Saving…" : value >= 100 && !done ? "Mark finished" : "Save"}
        </button>
      </div>
      <div className="inline small">
        {source === "hardcover" && <span className="muted row" style={{ gap: 4 }}><SyncIcon width={14} height={14} /> synced from Hardcover</span>}
        {!done && <button type="button" className="link muted" onClick={() => setGiving(true)}>Done with it</button>}
        {!done && sitOut && <button type="button" className="link muted" onClick={sitOut}>Read it before · sitting out</button>}
        <span className="muted" style={{ marginLeft: "auto" }}>{posHint(value, live)}</span>
      </div>
      {save.isError && <p className="error">{describeError(save.error)}</p>}
    </form>
  );
}

/** "= 46%" or "= p. 127" beside the editor: the same spot in the other unit, so a change is easy to sanity-check. */
function posHint(percent: number, ctx: PosCtx): string {
  if (ctx.myFormat === "percent") return ctx.pages ? `p. ${pageAt(percent, ctx.pages)}` : "";
  return `${percent}%`;
}
